import "./CancelOrderButton.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import React, { FC } from "react";

import { getString } from "../../uistringlabels/uiStringUtils";
import { CancelOrderModal } from "./CancelOrderModal";
import {
  CancellationReason,
  CancelOrderButtonProps,
  isCancellableStatus,
  isHopCancellable,
  shouldHideCancelButton,
} from "./types";

const BUTTON_LABEL = getString("hardwareOrderCancellation.button.label");
const DISABLED_TOOLTIP = getString("hardwareOrderCancellation.button.disabledTooltip");
const HOP_DISABLED_TOOLTIP = getString("hardwareOrderCancellation.button.hopDisabledTooltip");
const ButtonWithTooltip = withTooltip()(Button);

/**
 * Button component for initiating hardware order cancellation.
 * Uses WithModal to display the confirmation modal as a popup overlay.
 *
 * Renders based on order status:
 * - NEW/PROCESSING: Enabled button that opens modal
 * - SHIPPED/DELIVERED: Disabled button with tooltip
 * - CANCELLED: Hidden
 *
 * @param props - Component props
 * @returns Cancel order button or null if hidden
 */
export const CancelOrderButton: FC<CancelOrderButtonProps> = ({
  orderStatus,
  orderId,
  orderDetails,
  cancellationState,
  onConfirmCancellation,
  orderItems,
}) => {
  if (shouldHideCancelButton(orderStatus)) {
    return null;
  }

  const isCancellable = isCancellableStatus(orderStatus);
  const hopCancellable = isHopCancellable(orderItems || []);

  const buttonClassName = `cancel-order-button--destructive${
    !isCancellable || !hopCancellable ? " cancel-order-button--disabled" : ""
  }`;

  if (!isCancellable || !hopCancellable) {
    return (
      <ButtonWithTooltip
        variant={ButtonVariant.Secondary}
        disabled
        className={buttonClassName}
        aria-label={`Cancel order ${orderId}`}
        data-testid="cancel-order-button"
        tooltipText={!isCancellable ? DISABLED_TOOLTIP : HOP_DISABLED_TOOLTIP}
      >
        {BUTTON_LABEL}
      </ButtonWithTooltip>
    );
  }

  const renderModal = ({ close }: RenderModalFunctionParams) => (
    <CancelOrderModal
      onClose={close}
      onConfirm={(reason: CancellationReason) => {
        onConfirmCancellation(reason);
      }}
      orderDetails={orderDetails}
      cancellationState={cancellationState}
    />
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={open}
          className={buttonClassName}
          aria-label={`Cancel order ${orderId}`}
          data-testid="cancel-order-button"
          disabled={!isCancellable}
        >
          {BUTTON_LABEL}
        </Button>
      )}
    </WithModal>
  );
};

export default CancelOrderButton;
